// src/components/QuestionsList/QuestionsList.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ChevronRight, AlertCircle, Zap, Hash, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';

const QuestionsList = ({ questions = [], pagination = {}, onPageChange = () => {} }) => {
  const navigate = useNavigate();

  const getStatusStyles = (status) => {
    const styles = {
      Processing: {
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        border: 'border-amber-200',
        icon: <div className="h-3 w-3 rounded-full bg-amber-400 animate-pulse mr-1.5" />,
      },
      Completed: {
        bg: 'bg-emerald-100',
        text: 'text-emerald-800',
        border: 'border-emerald-200',
        icon: <div className="h-3 w-3 rounded-full bg-emerald-500 mr-1.5" />,
      },
      Error: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-200',
        icon: <div className="h-3 w-3 rounded-full bg-red-500 mr-1.5" />,
      }
    };
    return styles[status] || styles.Error;
  };

  // Generate an array of page numbers to display
  const getPageNumbers = () => {
    const { currentPage, totalPages } = pagination;
    const delta = 1; // Number of pages to show on each side of current page
    const range = [];
    
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    // Add first page
    if (currentPage - delta > 2) {
      range.unshift('...');
    }
    if (totalPages > 1) {
      range.unshift(1);
    }

    // Add last page
    if (currentPage + delta < totalPages - 1) {
      range.push('...');
    }
    if (totalPages > 1 && totalPages !== 1) {
      range.push(totalPages);
    }

    return range;
  };

  if (questions.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
        <div className="text-6xl mb-4">🔍</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No research found</h3>
        <p className="text-gray-500 max-w-md mx-auto mb-6">
          We couldn't find any research queries that match your criteria. Try adjusting your search or start a new research.
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Start new research
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {questions.map((question) => {
          const statusStyle = getStatusStyles(question.status);
          const date = new Date(question.date_created);
          const formattedDate = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }).format(date);

          return (
            <div
              key={question.id}
              className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden cursor-pointer"
              onClick={() => navigate(`/history/query/${question.id}`)}
            >
              <div className="p-5">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-grow space-y-3">
                    {/* Header with status and timestamp */}
                    <div className="flex flex-wrap items-center gap-3">
                      <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
                        {statusStyle.icon}
                        {question.status}
                      </div>
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {formattedDate}
                      </span>
                    </div>
                    
                    {/* Question type and content */}
                    <div>
                      {question.question_type && (
                        <span className="inline-block bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded mr-2 mb-2">
                          {question.question_type}
                        </span>
                      )}
                      <h3 className="font-medium text-lg text-gray-900">
                        {question.question}
                      </h3>
                    </div>
                    
                    {/* Result preview */}
                    {question.result && (
                      <p className="text-gray-600 line-clamp-2 text-sm">
                        {question.result.substring(0, 150)}...
                      </p>
                    )}

                    {/* Stats and metrics */}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                      {question.completion_time && (
                        <span className="flex items-center">
                          <Zap className="h-3 w-3 mr-1 text-amber-500" />
                          {question.completion_time.toFixed(2)}s
                        </span>
                      )}
                      {question.charges && (
                        <span className="flex items-center">
                          <Hash className="h-3 w-3 mr-1 text-indigo-500" />
                          {question.charges} credits used
                        </span>
                      )}
                    </div>

                    {/* Error message */}
                    {question.error_message && (
                      <div className="mt-3 text-sm text-red-600 flex items-center gap-1 bg-red-50 p-2 rounded">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{question.error_message}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Right arrow */}
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-gray-100 group-hover:bg-indigo-100 transition-colors">
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Pagination controls */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white py-4 mt-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
                pagination.currentPage === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-indigo-600 hover:bg-indigo-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
                pagination.currentPage === pagination.totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-indigo-600 hover:bg-indigo-50'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                </span> of{' '}
                <span className="font-medium">{pagination.totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                    pagination.currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-indigo-50 hover:text-indigo-600'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {getPageNumbers().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === '...' ? (
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white">
                        ...
                      </span>
                    ) : (
                      <button
                        onClick={() => onPageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                          page === pagination.currentPage
                          ? 'bg-indigo-600 text-white focus:z-10'
                          : 'bg-white text-gray-500 hover:bg-indigo-50 hover:text-indigo-600'
                        }`}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}
                
                <button
                  onClick={() => onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                    pagination.currentPage === pagination.totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-500 hover:bg-indigo-50 hover:text-indigo-600'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionsList;